// Single source of truth for the words in LPRecruiting (portal DOM) and
// LPRecruitingPDF (print). Rendered by ../documents/DocContent.jsx and
// ../pdf/PdfContent.jsx — the block shape is documented in DocContent.jsx.
// pdf* fields and the dom:/pdf: switches mark where the print copy
// deliberately diverges from the portal copy.

export const docTitle = "GNF LP recruiting";

export const cover = {
  eyebrow: "Growth · 03",
  number: "03",
  title: "Recruiting Limited Partners",
  summary: "How to talk about GNF, who to invite, and how to turn a casual coffee into a founding LP commitment.",
  accent: "aqua",
};

export const blocks = [
  {
    type: "lede",
    text: "A chapter lives or dies by its LPs. This is how you talk about GNF, who you should invite, and how to turn \"sounds cool\" into a signed check.",
  },
  { type: "heading", level: 1, text: "Why LPs say yes" },
  {
    type: "p",
    runs: [
      "LPs don't join for ROI. There isn't one. They join for a feeling: that they're doing something unambiguously good, that they're connected to the most interesting people in town, and that the money actually reaches a founder who needed it. Lead with that.",
    ],
  },
  {
    type: "callout",
    label: "The One-Liner",
    runs: [
      "\"It's $500 a year. We pool it with 8 to 10 LPs, give $1,000 grants to local founders we pick together, and it's the most fun quarterly meeting on your calendar.\"",
    ],
  },
  { type: "heading", level: 1, text: "Who to invite" },
  {
    type: "p",
    runs: [
      "Target people who are already doing the thing, not people looking for an excuse to be seen.",
    ],
  },
  { type: "heading", level: 2, text: "Good fits" },
  {
    type: "list",
    items: [
      {
        runs: [
          { b: "Past or present entrepreneurs." },
          " They know how much $1,000 meant at the beginning.",
        ],
      },
      {
        runs: [
          { b: "Active community builders." },
          " Chambers of commerce, incubator staff, local org leaders.",
        ],
      },
      {
        runs: [
          { b: "Creatives with networks." },
          " Designers, photographers, writers who know every founder in town.",
        ],
      },
      {
        runs: [
          { b: "Operators at growth-stage companies." },
          " Directors, VPs, founder-adjacent folks with $500 to spend and taste to lend.",
        ],
        pdfRuns: [
          { b: "Operators at growth-stage companies." },
          " Directors, VPs, founder-adjacent folks with $500 to spend.",
        ],
      },
      {
        runs: [
          { b: "Retired founders / angels." },
          " They've done the real thing; this is pure joy for them.",
        ],
      },
    ],
  },
  { type: "heading", level: 2, text: "Avoid" },
  {
    type: "list",
    items: [
      { runs: ["Career networkers who want a title, not a role."] },
      { runs: ["Anyone pitching you their own thing within the first 10 minutes."] },
      { runs: ["Disengaged funders looking for another logo on their LinkedIn."] },
      { runs: ["Folks who'd be uncomfortable in a dive bar with a pitch deck on a TV."] },
    ],
  },
  { type: "heading", level: 1, text: "The outreach playbook", pdfBreak: true },
  { type: "heading", level: 2, text: "Step 1. Start with 10 warm asks" },
  {
    type: "p",
    runs: [
      "Your first LPs should be people you'd text about dinner, not people you cold-DM on LinkedIn. Make a list of 20 and aim to close 10. Start there.",
    ],
    pdfRuns: [
      "Your first LPs should be people you'd text about dinner, not people you cold-DM on LinkedIn. Make a list of 20 and aim to close 10.",
    ],
  },
  { type: "heading", level: 2, text: "Step 2. Coffee, not pitch deck" },
  {
    type: "p",
    runs: [
      "Meet in person if you can. 20 minutes. Tell them the story: why GNF exists, why you're starting a chapter, why ",
      { i: "they" },
      " specifically came to mind. Don't send them a deck first. The magic is the conversation.",
    ],
    pdfRuns: [
      "Meet in person if you can. 20 minutes. Tell them the story: why GNF exists, why you're starting a chapter, why they specifically came to mind.",
    ],
  },
  { type: "heading", level: 2, text: "Step 3. Name the ask clearly" },
  {
    type: "p",
    runs: [
      "Don't soft-pedal. \"I'd love to have you as a founding LP. It's $500 a year, four meetings, and you get to give away $4,000 in grants to founders you pick. Want in?\" Clear beats clever.",
    ],
    pdfRuns: [
      "\"I'd love to have you as a founding LP. It's $500 a year, four meetings, and you get to give away $4,000 in grants to founders you pick. Want in?\" Clear beats clever.",
    ],
  },
  { type: "heading", level: 2, text: "Step 4. Send the one-pager within 24 hours" },
  {
    type: "p",
    runs: [
      "Attach the Chapter Handbook PDF and a link to the chapter landing page. Don't overload. One email, two links, a Venmo/Stripe link when they're ready.",
    ],
    pdfRuns: [
      "Attach the Chapter Handbook PDF and a link to the chapter landing page. One email, two links, a payment link when they're ready.",
    ],
  },
  { type: "heading", level: 2, text: "Step 5. Close fast" },
  {
    type: "p",
    runs: [
      "Momentum matters. If someone says yes, get them paid and in the Slack channel within a week. Waiting makes a casual yes into a polite no.",
    ],
    pdfRuns: [
      "Momentum matters. If someone says yes, get them paid and in the Slack channel within a week. Waiting turns a casual yes into a polite no.",
    ],
  },
  { type: "heading", level: 1, text: "Common objections", pdfBreak: true },
  { type: "heading", level: 2, text: "\"I don't really know enough about startups.\"" },
  {
    type: "p",
    runs: [
      "Good. LPs aren't supposed to be investors. They're supposed to be people with local knowledge and taste. You rate pitches on gut, kindness, and what your city needs, not term sheets.",
    ],
  },
  { type: "heading", level: 2, text: "\"Is this tax-deductible?\"" },
  {
    type: "p",
    runs: [
      "Yes. GNF operates under a 501(c)(3) fiscal sponsor, so LP contributions are tax-deductible. You'll get a receipt for your records. It's still a gift, not an investment, and there's no equity or expected return.",
    ],
  },
  { type: "heading", level: 2, text: "\"Why only $500? Why not more?\"" },
  {
    type: "p",
    runs: [
      "$500 keeps the door wide open. We want LPs from every income level. The point is community, not capital. If they want to give more, they can sponsor a quarter or support a grantee directly.",
    ],
    pdfRuns: [
      "$500 keeps the door wide open. We want LPs from every income level. The point is community, not capital.",
    ],
  },
  { type: "heading", level: 2, text: "\"I'm too busy.\"" },
  {
    type: "p",
    runs: [
      "One hour a month of async pitch review. Four in-person meetings a year, two-hour minimum. That's it. No Slack FOMO required.",
    ],
    pdfRuns: [
      "One hour a month of async pitch review. Four in-person meetings a year, two-hour minimum. That's it.",
    ],
  },
  { type: "heading", level: 1, text: "The first meeting pitch", pdf: false },
  {
    type: "p",
    runs: [
      "Here's a script you can adapt when you host your first all-hands LP dinner. Don't read it verbatim. It's a skeleton.",
    ],
    pdf: false,
  },
  {
    type: "script",
    label: "Opening · 3 minutes",
    text: "\"Thanks for being here. In Buffalo in 2023, two people had a weird idea. Pool $500 from 10 friends and give $1,000 to a local founder every quarter. No paperwork, no equity, just belief capital. Two years later there are [N] chapters doing the same thing. Tonight we start ours.\"",
  },
  {
    type: "script",
    label: "The Ask · 2 minutes",
    text: "\"What I'm asking is $500 a year, one hour a month to rate pitches, and four dinners like this one. In return you'll hear from the most interesting founders in [city] before anyone else does. And four times a year, one of them walks out with $1,000 because of us.\"",
  },
  {
    type: "script",
    label: "The Close · 1 minute",
    text: "\"I'll send a short follow-up tomorrow with the payment link and the handbook. If you're in, I'd love to have you as one of our founding LPs.\"",
  },
  { type: "heading", level: 1, text: "Email templates", pdfBreak: true },
  { type: "heading", level: 2, text: "The first outreach", pdfText: "First outreach" },
  {
    type: "emailTemplate",
    subject: "A weird little thing I'm starting in [city]",
    paras: [
      "Hey [name],",
      "I'm starting a local chapter of Good Neighbor Fund. It's a community group of ~10 people who pool $500/year and give out $1,000 grants to early-stage founders every quarter. No equity, no paperwork, just belief capital for people taking their first swing.",
      "You came to mind because of [specific reason: their network, their past company, their eye for talent]. I'd love to tell you more over coffee. 20 minutes, on me. This Thursday or next?",
      "[signature]",
    ],
    pdfParas: [
      "Hey [name],",
      "I'm starting a local chapter of Good Neighbor Fund. It's a community group of ~10 people who pool $500/year and give out $1,000 grants to early-stage founders every quarter. No equity, no paperwork, just belief capital for people taking their first swing.",
      "You came to mind because of [specific reason]. I'd love to tell you more over coffee. 20 minutes, on me. This Thursday or next?",
      "[signature]",
    ],
  },
  { type: "heading", level: 2, text: "The follow-up after coffee", pdfText: "Follow-up after coffee" },
  {
    type: "emailTemplate",
    subject: "Official ask: GNF [city]",
    paras: [
      "[name],",
      "Great catching up yesterday. As promised, the Chapter Handbook is attached and the chapter page is live at [link].",
      "Formal ask: would you come on as a founding LP? $500 for the first year, four meetings, ~1 hour/month of pitch review. Payment link: [link]. If you're in, reply here and I'll send you the Slack invite and first meeting date.",
      "No pressure either way, and thanks for hearing me out.",
      "[signature]",
    ],
  },
  { type: "heading", level: 1, text: "Retention: keeping LPs for year two", pdfBreak: true },
  {
    type: "p",
    runs: ["Year one is exciting. Year two is the cliff. Here's what keeps people around:"],
  },
  {
    type: "list",
    items: [
      {
        runs: [
          { b: "In-person meetings that feel like a dinner party." },
          " Not a Zoom call. Not a conference room.",
        ],
      },
      {
        runs: [
          { b: "Shoutouts." },
          " Name each LP publicly when you announce grantees. People love seeing their name.",
        ],
        pdfRuns: [{ b: "Shoutouts." }, " Name each LP publicly when you announce grantees."],
      },
      {
        runs: [
          { b: "Grantee updates." },
          " Six months after the grant, share what the founder did with it. This is the whole point.",
        ],
        pdfRuns: [
          { b: "Grantee updates." },
          " Six months after the grant, share what the founder did with it.",
        ],
      },
      {
        runs: [{ b: "Make renewal stupid easy." }, " One-click Stripe, email in January, done."],
      },
      { runs: [{ b: "Ask them to bring a friend." }, " The best LPs recruit the next LPs."] },
    ],
  },
  {
    type: "callout",
    label: "Stuck?",
    runs: [
      "If recruiting stalls, ping the national Slack. Other Chapter Directors have been through the same wall, and you don't have to figure it out alone.",
    ],
  },
];
